import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
    obtenerSesionActiva,
    obtenerSesionActivaPorId,
    OperadorPOSGrant,
    SesionActivaResponse,
} from '../services/pos/sesionPOSService';
import {
    fetchPuntoDeVenta,
    PuntoDeVenta,
} from '../services/pos/puntoDeVentaService';
import { useLocation } from 'react-router-dom';

interface POSContextType {
    sesionActiva: SesionActivaResponse | null;
    puntoDeVenta: PuntoDeVenta | null;
    sucursalId: number | null;
    cargandoSesion: boolean;
    operadorActivo: OperadorPOSGrant | null;
    setOperadorActivo: React.Dispatch<React.SetStateAction<OperadorPOSGrant | null>>;
}

const POSContext = createContext<POSContextType | undefined>(undefined);

export const usePOS = () => {
    const context = useContext(POSContext);
    if (!context) {
        throw new Error('usePOS debe usarse dentro de un POSProvider');
    }
    return context;
};

const parseRequestedSessionId = (state: unknown): number | null => {
    const raw = Number((state as { sesionId?: unknown } | null)?.sesionId);
    return Number.isInteger(raw) && raw > 0 ? raw : null;
};

/** Carga en vuelo: qué sesión persigue y con qué generación se identifica. */
interface PendingLoad {
    requestedSessionId: number | null;
    generation: number;
}

/**
 * Autoridad estable de la sesión POS mostrada.
 *
 * La sesión se elige por ``location.state.sesionId`` (lista de POS, "Ver
 * reservas", etc.). Una navegación hija que no trae ``sesionId`` conserva la
 * sesión ya resuelta y el operador identificado por PIN: con varias cajas
 * abiertas el selector singular (``obtenerSesionActiva``) respondería 409 y se
 * perdería la caja elegida. Solo un ``sesionId`` explícito distinto recarga
 * el contexto (y descarta el grant, que está ligado a la sesión anterior).
 *
 * Autoridad de carga completa: una sesión solo se considera resuelta (y se
 * publica) cuando sesión, punto de venta y sucursal están disponibles; nunca
 * se expone un estado parcial. Si mientras la carga está en vuelo llega una
 * navegación hija sin ``sesionId``, la carga se ADOPTA (no se invalida): su
 * resultado sigue siendo la autoridad y ``cargandoSesion`` termina siempre.
 * Solo un cambio real de sesión o el desmontaje invalidan una carga; sus
 * respuestas tardías no escriben nada.
 */
export const POSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const [sesionActiva, setSesionActiva] = useState<SesionActivaResponse | null>(null);
    const [puntoDeVenta, setPuntoDeVenta] = useState<PuntoDeVenta | null>(null);
    const [sucursalId, setSucursalId] = useState<number | null>(null);
    const [cargandoSesion, setCargandoSesion] = useState(true);
    const [operadorActivo, setOperadorActivo] = useState<OperadorPOSGrant | null>(null);
    const requestedSessionId = parseRequestedSessionId(location.state);
    // Sesión COMPLETAMENTE resuelta (sesión + punto + sucursal publicados).
    const resolvedSessionRef = useRef<number | null>(null);
    // Carga en vuelo, si la hay.
    const pendingLoadRef = useRef<PendingLoad | null>(null);
    const loadGenerationRef = useRef(0);

    useEffect(() => {
        // Una carga cuya generación ya fue invalidada (p. ej. desmontaje y
        // remontaje en StrictMode) no cuenta como carga en vuelo.
        const pending = pendingLoadRef.current !== null
            && pendingLoadRef.current.generation === loadGenerationRef.current
            ? pendingLoadRef.current
            : null;
        if (requestedSessionId === null) {
            // Sin sesionId explícito: contexto estable si ya hay sesión
            // resuelta, o adopción de la carga en vuelo si aún no terminó.
            if (resolvedSessionRef.current !== null || pending !== null) return;
        } else {
            // Mismo sesionId ya resuelto (otro objeto ``state``): nada que recargar.
            if (requestedSessionId === resolvedSessionRef.current) return;
            // Misma sesión ya en vuelo: se adopta esa carga.
            if (pending !== null && pending.requestedSessionId === requestedSessionId) return;
        }

        // Nueva carga: invalida cualquier carga anterior (cambio real de sesión).
        const generation = ++loadGenerationRef.current;
        const active = () => generation === loadGenerationRef.current;
        pendingLoadRef.current = { requestedSessionId, generation };

        const cargarSesionActiva = async () => {
            setCargandoSesion(true);
            setOperadorActivo(null);
            try {
                const sesion = requestedSessionId !== null
                    ? await obtenerSesionActivaPorId(requestedSessionId)
                    : await obtenerSesionActiva();
                if (!active()) return;
                const puntoVenta = await fetchPuntoDeVenta(sesion.punto_de_venta);
                if (!active()) return;
                // Publicación atómica: nunca sesión sin punto/sucursal.
                resolvedSessionRef.current = sesion?.id ?? null;
                setSesionActiva(sesion);
                setPuntoDeVenta(puntoVenta);
                setSucursalId(puntoVenta.sucursal);
            } catch (error) {
                if (!active()) return;
                /* Browser diagnostics omitted. */
                resolvedSessionRef.current = null;
                setSesionActiva(null);
                setPuntoDeVenta(null);
                setSucursalId(null);
                setOperadorActivo(null);
            } finally {
                if (active()) {
                    pendingLoadRef.current = null;
                    setCargandoSesion(false);
                }
            }
        };

        cargarSesionActiva();
    }, [requestedSessionId]);

    // Solo el desmontaje (no una navegación) invalida la carga en vuelo.
    useEffect(() => () => { loadGenerationRef.current += 1; }, []);

    return (
        <POSContext.Provider value={{ sesionActiva, puntoDeVenta, sucursalId, cargandoSesion, operadorActivo, setOperadorActivo }}>
            {children}
        </POSContext.Provider>
    );
};

